/**
 * The support-bundle wire contract, shared by the browser, the PF-Connect
 * backend proxy, and the Orange Tux receiver.
 *
 * The bundle is `multipart/form-data` with per-member compression — not a zip,
 * and there is no manifest. AGI-Web reversed this twice in review before
 * freezing it: under a JSON envelope a base64 screenshot forces a global
 * body-parser ceiling to serve one endpoint, and base64 ships about a third
 * larger. Under multipart the large parts belong to the multipart parser and
 * the JSON body parser is never in the path.
 *
 * ---
 *
 * ⚠ RESPONSES ARE ENVELOPED AT **BOTH** HOPS, INDEPENDENTLY.
 *
 * The receiver wraps its reply in `{success, data:{...}}`, and so does this
 * app — `apiResponseWrapper()` is mounted across all of `/api` in
 * `backend/src/app.ts`. So a reference is read out of `data.reference` twice
 * on its way to the browser, by two different pieces of middleware neither of
 * which is visible from the call site.
 *
 * This was got wrong once at each layer, independently, by people who already
 * knew about the other one. If a third service ever joins the chain, assume it
 * envelopes too and check rather than reading a bare key — the failure is
 * silent and inverted: every accepted bundle reports as a failure, and the
 * user resends something the receiver already has.
 */

/** Identifies the originating product to a receiver shared with other apps. */
export const PRODUCT_ID = 'pf-connect'

/** Multipart field names. Both hops use the same names. */
export const PART_MESSAGE = 'message'
export const PART_PRODUCT_ID = 'productId'
export const PART_CLIENT_VERSION = 'clientVersion'
export const PART_PLATFORM = 'platform'
export const PART_ENVIRONMENT = 'environment'
export const PART_TENANT_ID = 'tenantId'
export const PART_USER_ID = 'userId'
/**
 * Submitter identity, server-derived from the verified JWT.
 *
 * `userId` is a Cognito `sub` from PF-Connect's own user pool, so the receiver
 * has no directory row to resolve it against — without a name and an email the
 * triage console shows a bare UUID and nobody can reach the person who filed
 * the report. Both are optional: a JWT missing a claim must not cost a bundle,
 * and an omitted part reads as "unknown" where an empty string would be stored
 * as a value.
 */
export const PART_USER_NAME = 'userName'
export const PART_USER_EMAIL = 'userEmail'
export const PART_SCREENSHOT_STATE = 'screenshotState'
export const PART_MODEL_STATE = 'modelState'
export const PART_DIAGNOSTICS = 'diagnostics'
export const PART_SCREENSHOT = 'screenshot'
export const PART_MODEL = 'model'

/**
 * Every part name the contract defines — the maximal bundle, which is the
 * shape a real authenticated client always sends.
 *
 * THE canonical list, not a convenience re-export. The receiver's multipart
 * limits are sized against this count, and a limit set *at* it rejected 100%
 * of production traffic once already (#766). Anything that needs to know "how
 * many parts are there" must read this rather than count for itself.
 *
 * Adding a `PART_*` above means adding it here in the same edit. Nothing can
 * enforce that mechanically across two repositories, which is exactly why the
 * count lives in one place on this side.
 */
export const ALL_PART_NAMES = [
  PART_MESSAGE,
  PART_PRODUCT_ID,
  PART_CLIENT_VERSION,
  PART_PLATFORM,
  PART_ENVIRONMENT,
  PART_TENANT_ID,
  PART_USER_ID,
  PART_USER_NAME,
  PART_USER_EMAIL,
  PART_SCREENSHOT_STATE,
  PART_MODEL_STATE,
  PART_DIAGNOSTICS,
  PART_SCREENSHOT,
  PART_MODEL,
] as const

export const FILENAME_DIAGNOSTICS = 'diagnostics.json.gz'
export const FILENAME_SCREENSHOT = 'screenshot.png'
export const FILENAME_MODEL = 'model.json.gz'

/** Total request body budget. Raised over AGI's 4MB because the model is a
 *  first-class member here and theirs deliberately carries no document. */
export const MAX_BUNDLE_BYTES = 10 * 1024 * 1024

/**
 * Per-part caps, checked in addition to the total.
 *
 * A multipart parser's size limit applies PER FILE, so a single total-sized cap
 * would let three parts buffer three times the budget in memory before anything
 * noticed. Each part is bounded on its own, and the sum is bounded again.
 *
 * The model gets the largest share because it is the artifact most likely to be
 * genuinely big: the largest known customer model is 3.1MB of raw JSON, which
 * gzips to a few hundred KB, so this is generous rather than tight.
 */
export const MAX_DIAGNOSTICS_BYTES = 5 * 1024 * 1024
export const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
export const MAX_MODEL_BYTES = 10 * 1024 * 1024

/** Free text is capped on both sides, so neither side's assumption can drop a
 *  bundle that the other would have accepted. */
export const MAX_MESSAGE_CHARS = 10_000

export const MAX_USER_NAME_CHARS = 255
/** RFC 5321 maximum reverse-path length. */
export const MAX_USER_EMAIL_CHARS = 320

export const CONSOLE_BUFFER_SIZE = 500
export const BREADCRUMB_BUFFER_SIZE = 200
export const REQUEST_ID_BUFFER_SIZE = 50

/**
 * Why an artifact is or isn't in the bundle.
 *
 * A two-bit scheme — an opt-out flag plus whether the part is present — is
 * enough for a desktop app whose window always exists. In a browser it isn't: a
 * bundle sent from the dashboard has no model to send, which is neither an
 * opt-out nor a failed capture, and left as a bare absent part would read as
 * "capture failed" and send triage after a client bug that never happened.
 *
 * These cross the wire as text parts rather than being derived from booleans on
 * either side, so there is nothing to derive and nothing that can drift. They
 * are stored as columns because the operator action they enable — "show me
 * every bundle where capture failed", the client-bug signal — is a queue filter
 * over many bundles, which a field inside an opaque compressed blob could not
 * serve.
 *
 * `opted_out` with the part nonetheless present is contradictory: the receiver
 * discards the part and honours the opt-out.
 */
// `too_large` is the client shedding the screenshot after a 413 and retrying.
// Without it the retry would send `included` with no part attached, which is
// the one combination the scheme cannot explain. It is also the only trace a
// 413 leaves anywhere, since the rejected first request is never stored — so a
// cluster of it means the caps are wrong, not that capture is broken.
//
// `invalid` is what an unrecognised value is normalised to. Coercing to
// `failed` would be worse than useless: a screenshot arriving as `none_open`
// is a protocol bug, and calling it `failed` sends someone hunting a capture
// bug instead. It is deliberately not a state any healthy client can produce.
export const SCREENSHOT_STATES = [
  'included',
  'opted_out',
  'failed',
  'too_large',
  'invalid',
] as const
export const MODEL_STATES = [
  'included',
  'opted_out',
  'none_open',
  'failed',
  'invalid',
] as const

export type ScreenshotState = (typeof SCREENSHOT_STATES)[number]
export type ModelState = (typeof MODEL_STATES)[number]

export interface BundleMetadata {
  app_version: string
  build_date?: string
  environment: string
  region: string
  ecs_task_id?: string
  tenant_id: string
  user_id: string
  scopes: string[]
  browser: string
  os: string
  screen: string
  locale: string
  timezone: string
  /** The route the user was on — bugs outside the editor are a real class. */
  route: string
  feature_flags: Record<string, boolean>
}

export interface DiagnosticsConsoleEntry {
  level: 'log' | 'info' | 'warn' | 'error'
  message: string
  ts: number
}

export interface DiagnosticsBreadcrumb {
  action: string
  target?: string
  ts: number
}

export interface DiagnosticsRequestId {
  id: string
  method: string
  path: string
  status: number
  ts: number
}

/**
 * How many entries each channel lost, from the ring buffer's own window and
 * from byte capping combined. Absent keys mean nothing was dropped — triage
 * should be able to tell "quiet session" from "we threw your logs away".
 */
export interface TruncationReport {
  console_messages?: number
  breadcrumbs?: number
  request_ids?: number
}

export interface DiagnosticsPayload {
  source: typeof PRODUCT_ID
  client_ts: number
  console_messages: DiagnosticsConsoleEntry[]
  breadcrumbs: DiagnosticsBreadcrumb[]
  request_ids: DiagnosticsRequestId[]
  metadata: BundleMetadata
  truncation: TruncationReport
}

/**
 * Reference shown to the user and quoted to support. Minted by the receiver —
 * never fabricated client-side.
 *
 * `PFB-` rather than `PF-` because a two-letter prefix reads as a JIRA project
 * key, and the ticket raised from a bundle is `PIPE-nnn` in an instance that
 * also has `LAI` — support would be reading both aloud on the same call. The
 * body is Crockford base32 (no I, L, O or U) so it survives being dictated.
 */
export const REFERENCE_PATTERN = /^PFB-[0-9ABCDEFGHJKMNPQRSTVWXYZ]{6}$/
